import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { formatApiError } from '../services/api';
import { Key, Eye, EyeSlash, SignOut, CheckCircle } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const passwordChecks = (pw) => ({
  length: pw.length >= 8,
  upper: /[A-Z]/.test(pw),
  lower: /[a-z]/.test(pw),
  digit: /[0-9]/.test(pw),
});

const ForcePasswordChange = () => {
  const { user, logout, checkAuth } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const checks = passwordChecks(newPassword);
  const allValid = checks.length && checks.upper && checks.lower && checks.digit;
  const matches = newPassword.length > 0 && newPassword === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!allValid) {
      setError('Passwort erfüllt nicht alle Kriterien.');
      return;
    }
    if (!matches) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/api/auth/force-change-password`,
        { new_password: newPassword },
        { withCredentials: true }
      );
      await checkAuth();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6 bg-[#F7F8FA]"
      data-testid="force-password-change-page"
    >
      <div className="w-full max-w-md bg-white border border-[#E2E4E9] rounded-sm shadow-sm">
        <div className="p-6 border-b border-[#E2E4E9]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 bg-[#E60000] rounded-sm flex items-center justify-center">
              <Key weight="fill" className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold tracking-tight text-[#111418]">
                Passwort ändern
              </h2>
              <p className="text-xs text-[#525866]">Zwingend erforderlich</p>
            </div>
          </div>
          <p className="text-sm text-[#525866]">
            Ihr Passwort wurde vom Administrator zurückgesetzt. Bitte legen Sie
            jetzt ein neues persönliches Passwort fest, um fortzufahren.
          </p>
          {user?.email && (
            <p className="mt-3 text-xs text-[#8A94A6]">
              Angemeldet als: <span className="font-medium text-[#111418]">{user.email}</span>
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div
              className="p-3 bg-red-50 border border-red-200 rounded-sm text-red-700 text-sm"
              data-testid="force-password-error"
            >
              {error}
            </div>
          )}

          <div>
            <label className="overline block mb-2">Neues Passwort</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input-swiss w-full px-4 py-3 pr-12"
                placeholder="••••••••"
                required
                data-testid="force-new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8A94A6] hover:text-[#525866]"
              >
                {showPassword ? <EyeSlash weight="bold" size={20} /> : <Eye weight="bold" size={20} />}
              </button>
            </div>
          </div>

          <div>
            <label className="overline block mb-2">Passwort bestätigen</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input-swiss w-full px-4 py-3"
              placeholder="••••••••"
              required
              data-testid="force-confirm-password-input"
            />
          </div>

          <ul className="space-y-1 text-xs" data-testid="password-criteria">
            {[
              { ok: checks.length, label: 'Mindestens 8 Zeichen' },
              { ok: checks.upper, label: 'Mindestens 1 Großbuchstabe' },
              { ok: checks.lower, label: 'Mindestens 1 Kleinbuchstabe' },
              { ok: checks.digit, label: 'Mindestens 1 Ziffer' },
              { ok: matches, label: 'Passwörter stimmen überein' },
            ].map((c) => (
              <li
                key={c.label}
                className={`flex items-center gap-2 ${c.ok ? 'text-emerald-700' : 'text-[#8A94A6]'}`}
              >
                <CheckCircle weight={c.ok ? 'fill' : 'regular'} size={14} />
                {c.label}
              </li>
            ))}
          </ul>

          <button
            type="submit"
            disabled={loading || !allValid || !matches}
            className="btn-primary w-full py-3 text-base disabled:opacity-50"
            data-testid="force-password-submit-button"
          >
            {loading ? 'Wird gespeichert...' : 'Passwort speichern'}
          </button>

          <button
            type="button"
            onClick={logout}
            className="w-full py-2 text-sm text-[#525866] hover:text-[#E60000] flex items-center justify-center gap-2"
            data-testid="force-password-logout-btn"
          >
            <SignOut weight="bold" size={16} />
            Abbrechen und abmelden
          </button>
        </form>
      </div>
    </div>
  );
};

export default ForcePasswordChange;
